import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Hero } from "@/components/Hero";
import { COMING_SOON } from "@/context/CartContext";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { Manifesto } from "@/components/Manifesto";
import { ProductCard } from "@/components/ProductCard";
import { AnimalDetailModal } from "@/components/AnimalDetailModal";
import { NotifyList } from "@/components/NotifyList";
import { Footer } from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
    const [featured, setFeatured] = useState([]);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/products`, { params: { featured: true } })
            .then((r) => setFeatured(r.data))
            .catch(() => {});
    }, []);

    return (
        <main>
            <Seo
                title="Logan's Reptiles & Exotics — Captive-Bred Reptiles & Rare Exotics"
                siteName="Logan's Reptiles & Exotics"
                description="Rare captive-bred snakes, lizards, spiders, frogs and exotic mammals with live arrival guarantee. Bred in shadows, crafted for the wild."
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Store",
                    name: "Logan's Reptiles & Exotics",
                    description:
                        "Captive-bred reptiles and exotic animals with documented bloodlines.",
                }}
            />
            <Hero featured={featured} />
            <EditorialMarquee />

            <section className="mx-auto max-w-7xl px-6 py-24 sm:px-12 lg:py-32" data-testid="featured-section">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.7 }}
                            className="font-mono2 text-xs uppercase tracking-[0.35em] text-red-500"
                        >
                            Featured Specimens
                        </motion.p>
                        <motion.h2
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            className="mt-4 font-display text-2xl font-extrabold tracking-tight text-bone sm:text-3xl lg:text-4xl"
                        >
                            Currently in the shadows
                        </motion.h2>
                    </div>
                    <Link
                        to="/shop"
                        data-testid="featured-view-all-link"
                        className="group flex items-center gap-2 font-mono2 text-xs uppercase tracking-[0.25em] text-ash transition-colors duration-300 hover:text-red-400"
                    >
                        Full Collection
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </div>

                {COMING_SOON ? (
                    <div
                        data-testid="featured-coming-soon"
                        className="relative mt-12 overflow-hidden border border-red-950/60 bg-coal/60 py-20 text-center"
                    >
                        <div className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full bg-red-900/15 blur-[100px]" />
                        <span className="relative mx-auto flex h-3 w-3">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-600 opacity-60" />
                            <span className="relative inline-flex h-3 w-3 rounded-full bg-red-700" />
                        </span>
                        <p className="text-stroke-crimson mt-8 font-display text-3xl font-extrabold uppercase tracking-[0.15em] sm:text-5xl">
                            Coming Soon
                        </p>
                        <p className="mt-5 font-editorial text-lg italic text-ash">
                            The collection is being prepared. Something wild is on its way.
                        </p>
                    </div>
                ) : (
                    <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {featured.slice(0, 4).map((p, i) => (
                            <ProductCard key={p.id} product={p} index={i} onOpen={setSelected} />
                        ))}
                    </div>
                )}
            </section>

            <Manifesto />
            <NotifyList />

            <AnimalDetailModal product={selected} onClose={() => setSelected(null)} />
            <Footer />
        </main>
    );
}
